import type { Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferOutput } from "valibot";
import {
  array,
  boolean,
  fallback,
  maxLength,
  nonEmpty,
  nullish,
  object,
  objectAsync,
  pipe,
  record,
  safeParse,
  string,
  union,
  unknown,
} from "valibot";
import { dataName, dataSource, errorTranslation, label } from "../../utils/validation/base";
import { TextInput } from "../../components/inputs/TextInput";
import { useTranslation } from "../../state/i18n";
import { MAX_CHARACTERS_SM, TEXT_CONDITION_KEYS } from "../../constants";
import { useFormServices } from "../../services/formServices";
import DataSourceInput from "../DataSourceInput";
import { Show } from "solid-js";
import { isArray, isEmpty, isNil, startCase } from "lodash-es";
import type { FieldType } from "../../types";
import uuid from "../../utils/uuid";
import {
  fieldValueReferenceOfTypes,
  metaVariableValueReferenceOfTypes,
  variableValueReferenceOfTypes,
} from "../../utils/validation/references";
import { parseColor } from "@ark-ui/solid";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { dataSourceAsync } from "../../utils/validation/async";
import type FormId from "../../state/FormId";
import FieldValueReference from "../FieldValueReference";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import DetailModal from "../../components/modal/DetailModal";
import SimpleTable from "../../components/table/SimpleTable";
import ButtonLink from "../../components/link/ButtonLink";
import GenericValue from "../valueRendering/GenericValue";
import { MultiSelectWithSearchInput } from "../../components/menu/MultiSelectWithSearch";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil";
import { useModalManager } from "../../components/modal/ModalManager";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import VariableValueReference from "../../variables/VariableValueReference";
import { FieldButtonPreview, FieldIconButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import { Toggle } from "../../components/toggle/Toggle";
import PropertySection from "../PropertySection";
import SearchColoursInput from "../SearchColoursInput";
import { keyFromSelector } from "i18next";

const ALLOWED_FIELD_TYPES: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];
const ALLOWED_VARIABLE_TYPES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

const colors = () => array(color());
const color = () =>
  object({
    id: string(),
    color: string(),
    query: pipe(
      string(),
      nonEmpty(errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_SEARCH_QUERY_REQUIRED))),
      maxLength(MAX_CHARACTERS_SM, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
    ),
  });

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  data_source_configuration: nullish(object({ id: string(), mapping: record(string(), boolean()) })),
  default_value: nullish(string()),
  filter_fields: fallback(array(string()), []),
  colors: nullish(array(object({ color: string(), query: string() }))),
  allow_barcode: fallback(boolean(), false),
  remember_search: fallback(boolean(), true),
  required: fallback(boolean(), false),
  remember_input: fallback(boolean(), false),
});

const ResourceSchema = (formId: FormId) =>
  objectAsync({
    data_source_configuration: dataSourceAsync(formId),
  });

const DomainSchema = object({
  label_text: label(),
  data_name: dataName(),
  data_source_configuration: dataSource(),
  default_value: nullish(string()),
  filter_fields: array(
    union([
      fieldValueReferenceOfTypes(ALLOWED_FIELD_TYPES),
      metaVariableValueReferenceOfTypes(
        ["info.userId"],
        errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)),
      ),
      variableValueReferenceOfTypes(
        ALLOWED_VARIABLE_TYPES,
        errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION)),
      ),
    ]),
  ),
  colors: colors(),
  allow_barcode: boolean(),
  remember_search: boolean(),
  required: boolean(),
  remember_input: boolean(),
}) satisfies GenericSchema<InferOutput<ReturnType<typeof ResourceSchema>>>;

const ValueSchema = record(string(), unknown());

export type SearchFieldProperties = InferOutput<typeof DomainSchema>;
export type SearchFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const SearchField: Field<SearchFieldProperties, SearchFieldDtoType, ValueType> = {
  type: "search",
  category: "searchItems",
  widget: "com.moreapps:search:1",
  schema: DomainSchema,
  resourceSchema: ResourceSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    data_source_configuration: undefined,
    allow_barcode: false,
    remember_search: true,
    filter_fields: [],
    colors: [],
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "dataSource" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => String(value["id"]),
  renderValue: (value, { fieldConfig }) => {
    const { open } = useModalManager();

    const idColumnValue = String(value["id"]);
    const secondColumnKey = Object.keys(fieldConfig.properties.data_source_configuration.mapping).at(1);
    const secondColumnValue = value[secondColumnKey ?? ""];
    const buttonLabel = isEmpty(secondColumnValue) ? idColumnValue : `${idColumnValue}, ${secondColumnValue}`;

    return (
      <ButtonLink
        label={buttonLabel}
        onClick={() =>
          open({
            content: () => (
              <DetailModal
                title={fieldConfig.properties.label_text}
                iconTile={{
                  icon: SearchField.icon,
                  category: SearchField.category,
                }}
                content={() => {
                  const data = Object.entries(value).map(([key, value]) => ({
                    label: startCase(key),
                    value: <GenericValue value={value} />,
                  }));

                  return <SimpleTable data={data} />;
                }}
              />
            ),
          })
        }
      />
    );
  },
  icon: "searchCircle",
  titleKey: keyFromSelector(($) => $.SEARCH_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.SEARCH_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_source_configuration: keyFromSelector(($) => $.FIELD_PROPERTY_DATASOURCE),
    default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_QUERY),
    filter_fields: keyFromSelector(($) => $.FIELD_PROPERTY_FILTER_FIELDS),
    colors: keyFromSelector(($) => $.FIELD_PROPERTY_SEARCH_COLORS),
    allow_barcode: keyFromSelector(($) => $.FIELD_PROPERTY_ALLOW_BARCODE),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
    data_name: keyFromSelector(($) => $.DATA_NAME_LABEL),
    remember_search: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_SEARCH),
  },
  valueReferenceOptions: (id, props) => {
    if (isNil(props.data_source_configuration)) {
      return [];
    }
    return Object.keys(props.data_source_configuration.mapping).map((key) => {
      const referenceId = new FieldValueReference(id, [key]);
      const icon = getDynamicValueIcon(referenceId, SearchField.type);
      return {
        variant: "dynamicValue",
        value: referenceId,
        label: key,
        pill: props.data_name,
        icon,
      };
    });
  },
  fromDto: (dto) => {
    const { output } = safeParse(DomainSchema, {
      ...dto,
      colors: (dto.colors ?? []).map((color) => {
        let safeColor;
        try {
          safeColor = parseColor(color.color?.toLowerCase()).toString("hex");
        } catch {
          // Fallback to recognizable color
          safeColor = "#010101";
        }
        return { ...color, color: safeColor, id: uuid() };
      }),
      filter_fields: isArray(dto.filter_fields) ? dto.filter_fields : [],
    });
    return output as SearchFieldProperties;
  },
  postProcessFromDto: (domain, _allFields, variables) => ({
    ...domain,
    filter_fields: domain.filter_fields.map((item) => {
      const value = item.toString();

      if (value === "username") {
        return MetaVariableReference.from("info.userId");
      }

      return variables.some((variable) => variable.uid.toString() === value)
        ? VariableValueReference.from(value)
        : FieldValueReference.from(value);
    }),
  }),
  toDto: (domain) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      colors: domain.colors.map((color) => ({ ...color, id: undefined })),
      filter_fields: domain.filter_fields.map((item) => {
        if (item instanceof MetaVariableReference && item.id === "info.userId") {
          return "username";
        }

        return item.toString();
      }),
    });
    return output as SearchFieldDtoType;
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <div class="flex w-full gap-x-1">
          <FieldButtonPreview icon="search" text={t(($) => $.SEARCH_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
          <Show when={props.properties.allow_barcode}>
            <FieldIconButtonPreview icon="qrcode" size="md" />
          </Show>
        </div>
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            required
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
          />
          <TextInput
            label={t(props.propertyKeys.default_value)}
            value={props.data.properties.default_value ?? ""}
            onBlur={(e) => props.setValue("default_value", e.target.value)}
            errorMessage={props.getError("default_value")}
            info={{
              title: t(props.propertyKeys.default_value),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                property: props.propertyKeys.default_value,
              }),
              description: t(($) => $.FIELD_PROPERTY_DEFAULT_QUERY_DESCRIPTION),
            }}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_DATA)}>
          <DataSourceInput
            required
            label={t(props.propertyKeys.data_source_configuration)}
            value={props.data.properties.data_source_configuration}
            onSelect={(dataSourceConfig) => props.setValue("data_source_configuration", dataSourceConfig)}
            errorMessage={props.getError("data_source_configuration")}
            info={{
              title: t(props.propertyKeys.data_source_configuration),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                property: props.propertyKeys.data_source_configuration,
              }),
              description: t(($) => $.FIELD_PROPERTY_DATASOURCE_DESCRIPTION),
            }}
            sameWidth
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_FILTER)}>
          <MultiSelectWithSearchInput
            label={t(props.propertyKeys.filter_fields)}
            value={props.data.properties.filter_fields}
            errorMessage={props.getError("filter_fields")}
            options={{
              Fields: fieldService.getFieldsByTypes(ALLOWED_FIELD_TYPES).flatMap((field) => toFieldValueOptions(field)),
              Variables: [
                metaVariableOption("info.userId"),
                { Custom: variableService.getVariablesByTypes(ALLOWED_VARIABLE_TYPES).map(toVariableValueOption) },
              ],
            }}
            onSelect={(value) => props.setValue("filter_fields", value)}
            info={{
              title: t(props.propertyKeys.filter_fields),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                property: props.propertyKeys.filter_fields,
              }),
              description: t(($) => $.FIELD_PROPERTY_FILTER_FIELDS_DESCRIPTION),
            }}
            width="matchTrigger"
          />
          <SearchColoursInput
            queryColours={props.data.properties.colors ?? []}
            onChange={(colours) => props.setValue("colors", colours)}
            error={props.getError("colors")}
            nestedErrors={props.getNestedErrors("colors")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.allow_barcode)}
            checked={props.data.properties.allow_barcode}
            setChecked={(value) => props.setValue("allow_barcode", value)}
            description={t(($) => $.FIELD_PROPERTY_ALLOW_BARCODE_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.remember_search)}
            checked={props.data.properties.remember_search}
            setChecked={(value) => props.setValue("remember_search", value)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_SEARCH_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(SearchField);
